import { useEffect, useRef, type ReactNode } from 'react';
import { AlertCircle, X, LoaderCircle, Leaf, Waves, Lightbulb } from 'lucide-react';

export function Spinner({ label = 'Loading your workspace…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <LoaderCircle className="spin" size={24} />
      <span>{label}</span>
    </div>
  );
}
export function ErrorNotice({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="error-notice" role="alert">
      <AlertCircle size={19} />
      <span>{message}</span>
      {retry && (
        <button className="text-button" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = ref.current;
    const active = document.activeElement as HTMLElement;
    node?.showModal();
    return () => {
      node?.close();
      active?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={`modal ${wide ? 'wide' : ''}`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      aria-label={title}
    >
      <div className="modal-heading">
        <h2>{title}</h2>
        <button className="icon-button" onClick={onClose} aria-label="Close dialog">
          <X size={20} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
export function TopicIcon({ topic, size = 22 }: { topic: string; size?: number }) {
  return topic === 'plant' ? (
    <Leaf size={size} />
  ) : topic === 'bulb' ? (
    <Lightbulb size={size} />
  ) : (
    <Waves size={size} />
  );
}
export function ScienceArt({ topic, large = false }: { topic: string; large?: boolean }) {
  return (
    <svg
      className={`science-art ${large ? 'large' : ''}`}
      viewBox="0 0 260 170"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="140" cy="80" r="67" fill="currentColor" opacity=".08" />
      <path d="M35 140h180M47 146h154" stroke="currentColor" opacity=".15" strokeLinecap="round" />
      {topic === 'plant' ? (
        <>
          <path d="M108 117h58l-9 31h-40z" fill="currentColor" opacity=".25" />
          <path d="M137 120V49" stroke="currentColor" strokeWidth="3" />
          <path d="M137 99c-41 1-54-23-50-42 30-2 52 16 50 42Z" fill="currentColor" opacity=".55" />
          <path d="M137 78c36-1 52-23 46-42-28-1-47 17-46 42Z" fill="currentColor" opacity=".8" />
          <path d="m100 71 36 28m33-48-32 27" stroke="white" strokeOpacity=".6" strokeWidth="2" />
          <circle cx="70" cy="35" r="11" stroke="currentColor" opacity=".3" />
        </>
      ) : topic === 'bulb' ? (
        <>
          <path
            d="M155 109c0-13 22-25 22-49a37 37 0 0 0-74 0c0 24 22 36 22 49z"
            fill="currentColor"
            opacity=".15"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="m126 74 13 35 14-35m-28 42h31m-29 8h27m-25 8h22"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M75 65H60m155 0h-15M90 29l-9-10m109 10 9-10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path d="M129 139h21l-6 8h-9z" fill="currentColor" />
        </>
      ) : (
        <>
          <ellipse cx="133" cy="131" rx="67" ry="13" fill="currentColor" opacity=".12" />
          <ellipse cx="133" cy="130" rx="46" ry="6" stroke="currentColor" opacity=".4" />
          <path
            d="M131 37c-8 14-29 36-29 54a29 29 0 0 0 58 0c0-18-21-40-29-54Z"
            fill="currentColor"
            opacity=".32"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M116 88c-4 11 1 17 8 20" stroke="white" strokeWidth="4" strokeLinecap="round" />
          <path
            d="M76 102V72m0 0-5 7m5-7 5 7m102 22V55m0 0-5 7m5-7 5 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity=".6"
          />
          <circle cx="187" cy="30" r="12" stroke="currentColor" opacity=".3" />
          <path d="M187 9v-5m0 51v-5m21-20h5m-51 0h5" stroke="currentColor" opacity=".3" />
        </>
      )}
      <circle cx="44" cy="96" r="3" fill="currentColor" opacity=".25" />
      <circle cx="221" cy="102" r="4" fill="currentColor" opacity=".2" />
      <path d="M50 55h8m-4-4v8" stroke="currentColor" opacity=".3" />
    </svg>
  );
}
export function Empty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="empty">
      <Leaf size={30} />
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  );
}
